import { getSupabase, isSupabaseConfigured, bootstrapSupabaseConfig } from './client';
import { LEARNER_TABLES } from './tables';

/**
 * @typedef {{
 *   id: string;
 *   topicKey: string;
 *   publishedAt: string;
 *   isActive: boolean;
 *   titleHi: string | null;
 *   titleEn: string | null;
 *   createdBy: string | null;
 *   createdAt: string;
 *   updatedAt: string;
 * }} TopicRelease
 */

/** @param {Record<string, unknown>} row */
function mapTopicReleaseRow(row) {
  return {
    id: row.id,
    topicKey: row.topic_key,
    publishedAt: row.published_at,
    isActive: row.is_active !== false,
    titleHi: row.title_hi ?? null,
    titleEn: row.title_en ?? null,
    createdBy: row.created_by ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * @returns {Promise<TopicRelease[]>}
 */
export async function fetchTopicReleases() {
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) return [];

  const client = getSupabase();
  if (!client) return [];

  const { data, error } = await client
    .from(LEARNER_TABLES.topicReleases)
    .select('id, topic_key, published_at, is_active, title_hi, title_en, created_by, created_at, updated_at')
    .order('published_at', { ascending: false });

  if (error) {
    console.warn('[topicReleases] fetch failed:', error.message);
    throw error;
  }

  return (data ?? []).map(mapTopicReleaseRow);
}

/**
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function fetchIsAdmin(userId) {
  if (!userId) return false;
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) return false;

  const client = getSupabase();
  if (!client) return false;

  const { data, error } = await client
    .from(LEARNER_TABLES.admins)
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[topicReleases] admin check failed:', error.message);
    return false;
  }

  return Boolean(data?.user_id);
}

/**
 * @param {{
 *   topicKey: string;
 *   publishedAt: string;
 *   isActive?: boolean;
 *   titleHi?: string | null;
 *   titleEn?: string | null;
 *   createdBy: string;
 * }} input
 * @returns {Promise<TopicRelease>}
 */
export async function createTopicRelease(input) {
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) throw new Error('Topic releases storage is not configured.');

  const client = getSupabase();
  if (!client) throw new Error('Could not connect to topic releases storage.');

  const payload = {
    topic_key: input.topicKey.trim(),
    published_at: input.publishedAt,
    is_active: input.isActive !== false,
    title_hi: input.titleHi?.trim() || null,
    title_en: input.titleEn?.trim() || null,
    created_by: input.createdBy,
  };

  const { data, error } = await client
    .from(LEARNER_TABLES.topicReleases)
    .insert(payload)
    .select('id, topic_key, published_at, is_active, title_hi, title_en, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return mapTopicReleaseRow(data);
}

/**
 * @param {string} id
 * @param {{
 *   topicKey?: string;
 *   publishedAt?: string;
 *   isActive?: boolean;
 *   titleHi?: string | null;
 *   titleEn?: string | null;
 * }} patch
 * @returns {Promise<TopicRelease>}
 */
export async function updateTopicRelease(id, patch) {
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) throw new Error('Topic releases storage is not configured.');

  const client = getSupabase();
  if (!client) throw new Error('Could not connect to topic releases storage.');

  /** @type {Record<string, unknown>} */
  const payload = {};
  if (patch.topicKey != null) payload.topic_key = patch.topicKey.trim();
  if (patch.publishedAt != null) payload.published_at = patch.publishedAt;
  if (patch.isActive != null) payload.is_active = patch.isActive;
  if (patch.titleHi !== undefined) payload.title_hi = patch.titleHi?.trim() || null;
  if (patch.titleEn !== undefined) payload.title_en = patch.titleEn?.trim() || null;

  const { data, error } = await client
    .from(LEARNER_TABLES.topicReleases)
    .update(payload)
    .eq('id', id)
    .select('id, topic_key, published_at, is_active, title_hi, title_en, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return mapTopicReleaseRow(data);
}

/**
 * @param {string} id
 * @returns {Promise<TopicRelease>}
 */
export async function deactivateTopicRelease(id) {
  return updateTopicRelease(id, { isActive: false });
}
